import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Users, 
  Save, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2,
  CheckCircle, 
  AlertCircle,
  User,
  Upload,
  Download,
  Search,
  Filter,
  Eye,
  EyeOff,
  Settings,
  Activity,
  Calendar,
  Clock,
  Mail,
  Phone,
  MapPin,
  Globe,
  Shield,
  Key,
  Lock,
  Unlock,
  UserCheck,
  UserX,
  UserPlus,
  UserMinus,
  FileText,
  Database,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Star,
  Crown,
  Target,
  Zap,
  Monitor,
  HardDrive,
  Cpu,
  Network,
  Server,
  File,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  ArrowDown,
  Building,
  Home,
  Briefcase,
  Layers,
  GitBranch,
  TreePine,
  FolderTree,
  Workflow,
  Route,
  Navigation
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const UserProfileManagement = () => {
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [bulkOperations, setBulkOperations] = useState({});
  const [importExport, setImportExport] = useState({});
  const [activityMonitoring, setActivityMonitoring] = useState({});
  const [profileTemplates, setProfileTemplates] = useState([]);
  const [message, setMessage] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const [usersRes, bulkOpsRes, importExportRes, activityRes, templatesRes] = await Promise.all([
        MockDataService.getUsers(),
        MockDataService.getBulkOperations(),
        MockDataService.getImportExportSettings(),
        MockDataService.getUserActivityMonitoring(),
        MockDataService.getProfileTemplates()
      ]);
      
      setUsers(usersRes.data);
      setBulkOperations(bulkOpsRes.data);
      setImportExport(importExportRes.data);
      setActivityMonitoring(activityRes.data);
      setProfileTemplates(templatesRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load user data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('users', JSON.stringify(users));
      localStorage.setItem('bulkOperations', JSON.stringify(bulkOperations));
      localStorage.setItem('importExport', JSON.stringify(importExport));
      localStorage.setItem('activityMonitoring', JSON.stringify(activityMonitoring));
      localStorage.setItem('profileTemplates', JSON.stringify(profileTemplates));
      
      setMessage({ type: 'success', text: 'User settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save user settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkOperation = async (operation) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage({ type: 'success', text: `Bulk ${operation} completed successfully` });
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to perform bulk ${operation}` });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'suspended': return 'bg-red-50 text-red-700 border-red-200';
      case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown className="h-4 w-4" />;
      case 'agent': return <Users className="h-4 w-4" />;
      case 'user': return <User className="h-4 w-4" />;
      case 'super-admin': return <Shield className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Profile Management</h1>
          <p className="text-gray-600 mt-2">Manage user profiles and bulk operations</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadUserData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowUserForm(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profiles')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profiles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              User Profiles
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bulk'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="h-4 w-4 inline mr-2" />
              Bulk Operations
            </button>
            <button
              onClick={() => setActiveTab('import-export')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'import-export'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="h-4 w-4 inline mr-2" />
              Import/Export
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="h-4 w-4 inline mr-2" />
              Activity Monitoring
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Profile Templates
            </button>
          </nav>
        </div>

        {activeTab === 'profiles' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  User Profiles List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search users..."
                        className="pl-10 w-64"
                      />
                    </div>
                    <Select>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="super-admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <Button variant="outline" size="sm">
                      <Upload className="h-4 w-4 mr-2" />
                      Import
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {users.slice(0, 10).map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          {getRoleIcon(user.role)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{user.name}</h3>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={getStatusColor(user.status)}>
                              {user.status}
                            </Badge>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {user.role}
                            </Badge>
                            <span className="text-xs text-gray-500">Last active: {user.lastActive || '2 hours ago'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            Department: {user.department || 'Not assigned'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Created: {user.createdAt || '2024-01-15'}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingUser(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedUsers([...selectedUsers, user.id])}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBulkOperation('delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'bulk' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Bulk Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">User Status Operations</h3>
                    <div className="space-y-2">
                      <Button
                        onClick={() => handleBulkOperation('activate')}
                        className="w-full flex items-center gap-2"
                      >
                        <UserCheck className="h-4 w-4" />
                        Activate Selected Users
                      </Button>
                      <Button
                        onClick={() => handleBulkOperation('deactivate')}
                        variant="outline"
                        className="w-full flex items-center gap-2"
                      >
                        <UserX className="h-4 w-4" />
                        Deactivate Selected Users
                      </Button>
                      <Button
                        onClick={() => handleBulkOperation('suspend')}
                        variant="outline"
                        className="w-full flex items-center gap-2"
                      >
                        <Lock className="h-4 w-4" />
                        Suspend Selected Users
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Role Operations</h3>
                    <div className="space-y-2">
                      <Button
                        onClick={() => handleBulkOperation('assign-role')}
                        className="w-full flex items-center gap-2"
                      >
                        <Crown className="h-4 w-4" />
                        Assign Role to Selected
                      </Button>
                      <Button
                        onClick={() => handleBulkOperation('change-department')}
                        variant="outline"
                        className="w-full flex items-center gap-2"
                      >
                        <Building className="h-4 w-4" />
                        Change Department
                      </Button>
                      <Button
                        onClick={() => handleBulkOperation('reset-password')}
                        variant="outline"
                        className="w-full flex items-center gap-2"
                      >
                        <Key className="h-4 w-4" />
                        Reset Passwords
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Bulk Operations Settings</h3>
                      <p className="text-sm text-gray-600">Configure bulk operation preferences</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'import-export' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Import/Export Users
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Export Users</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="exportFormat">Export Format</Label>
                        <Select>
                          <SelectTrigger>
                            <SelectValue placeholder="Select format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="excel">Excel</SelectItem>
                            <SelectItem value="json">JSON</SelectItem>
                            <SelectItem value="xml">XML</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="exportFields">Fields to Export</Label>
                        <div className="space-y-2">
                          {['Name', 'Email', 'Role', 'Department', 'Status', 'Created Date'].map((field) => (
                            <div key={field} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={field.toLowerCase()}
                                defaultChecked
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <Label htmlFor={field.toLowerCase()} className="text-sm">{field}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button className="w-full flex items-center gap-2">
                        <Download className="h-4 w-4" />
                        Export Users
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Import Users</h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="importFile">Select File</Label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Drag and drop file here or click to browse</p>
                          <p className="text-xs text-gray-500 mt-1">CSV, Excel, JSON supported</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="importMode">Import Mode</Label>
                        <Select>
                          <SelectTrigger>
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">Create New Users</SelectItem>
                            <SelectItem value="update">Update Existing Users</SelectItem>
                            <SelectItem value="merge">Create or Update</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button variant="outline" className="w-full flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Import Users
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  User Activity Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Active Users</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-900">
                      {activityMonitoring.activeUsers || 45}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Online Now</span>
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {activityMonitoring.onlineNow || 12}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-900">Last 24h</span>
                    </div>
                    <div className="text-2xl font-bold text-yellow-900">
                      {activityMonitoring.last24h || 89}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Avg Session</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-900">
                      {activityMonitoring.avgSession || '2.3h'}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Recent User Activity</h3>
                  {users.slice(0, 5).map((user, index) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          {getRoleIcon(user.role)}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{user.name}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {user.lastActivity || 'Logged in'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.lastActive || '2 hours ago'}
                          </div>
                        </div>
                        <Badge variant="outline" className={getStatusColor(user.status)}>
                          {user.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Profile Templates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Available Templates</h3>
                      <p className="text-sm text-gray-600">Pre-configured user profile templates</p>
                    </div>
                    <Button className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Create Template
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {profileTemplates.map((template) => (
                      <div key={template.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{template.name}</h4>
                            <p className="text-sm text-gray-600">{template.description}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="text-sm text-gray-600">
                            <strong>Role:</strong> {template.role}
                          </div>
                          <div className="text-sm text-gray-600">
                            <strong>Department:</strong> {template.department}
                          </div>
                          <div className="text-sm text-gray-600">
                            <strong>Permissions:</strong> {template.permissions?.length || 0} permissions
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="flex-1">
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1">
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileManagement;
